import React, { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineZoomIn, AiOutlineZoomOut } from "react-icons/ai";
import { RiFullscreenFill, RiFullscreenExitLine } from "react-icons/ri";
import { Tooltip } from "@mui/material";
import "./ProcessFlowchartVisualization.css";
import { FaUser } from "react-icons/fa";
const CONNECTOR_COLORS = {
  default: "#e24a3b",
  active: "#c81e1e",
};
const relationStyles = {
  FS: { arrow: CONNECTOR_COLORS.default, label: "FS", dash: "" },
  SS: { arrow: CONNECTOR_COLORS.default, label: "SS", dash: "5,5" },
  FF: { arrow: CONNECTOR_COLORS.default, label: "FF", dash: "" },
  SF: { arrow: CONNECTOR_COLORS.default, label: "SF", dash: "5,5" },
};
const relationTooltips = {
  FS: "Finish to Start",
  SS: "Start to Start",
  FF: "Finish to Finish",
  SF: "Start to Finish",
};
// --- Add this OUTSIDE the component (top of file, with other constants) ---

const getStatusInfo = (step) => {
  const s = (step.status || "").toLowerCase();

  if (step.is_bypassed) {
    return {
      label: "Bypassed",
      icon: "â†’",
      color: "#7e22ce",
      bg: "#f3e8ff",
      border: "#d8b4fe",
      textColor: "#581c87",
      cardBorder: "#d8b4fe",
    };
  }

  if (s === "completed" || s === "complete") {
    return {
      label: "Completed",
      icon: "✓",
      color: "#16a34a",
      bg: "#dcfce7",        // was #f0fdf4 (too light)
      border: "#86efac",    // slightly stronger
      textColor: "#14532d",
      cardBorder: "#86efac",
    };
  }

  // In Progress
  if (step.actual_start && !step.actual_end) {
    return {
      label: "In Progress",
      icon: "▶",
      color: "#d97706",
      bg: "#fef3c7",        // was #fffbeb
      border: "#fcd34d",
      textColor: "#78350f",
      cardBorder: "#fcd34d",
    };
  }

  if (step.can_start) {
    return {
    label: "Ready to Start",
    icon: "▶",
    color: "#c2410c",
    bg: "#fff7ed",
    border: "#fdba74",
    textColor: "#7c2d12",
    cardBorder: "#fdba74",
  };
  }

  // Blocked
  if (step.blocked_by) {
    return {
      label: "Not Started",
      icon: "○",
      color: "#dc2626",
      bg: "#fee2e2",        // was #fef2f2
      border: "#fca5a5",
      textColor: "#7f1d1d",
      cardBorder: "#fca5a5",
    };
  }

  // Default
  return {
    label: "Not Started",
    icon: "○",
    color: "#dc2626",
    bg: "#fee2e2",
    border: "#fca5a5",
    textColor: "#7f1d1d",
    cardBorder: "#fca5a5",
  };
};

const getDurationAnalysis = (step) => {
  const da = step.duration_analysis;
  if (!da || da.actual_duration == null) return null;

  const diff = da.duration_difference ?? 0;
  if (diff === 0) return { label: "0h", color: "#6b7280" };
  if (diff > 0) return { label: `+${diff.toFixed(1)}h`, color: "#dc2626" };
  return { label: `${diff.toFixed(1)}h`, color: "#16a34a" };
};
const ProcessFlowchartVisualization = ({
  processSteps,
  onCardClick,
  selectedProcessId,
  onExternalCardClick,
}) => {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const flowchartWrapperRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(1200);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const wrapper = flowchartWrapperRef.current;
    if (!wrapper) return;

    const updateWidth = () => {
      const nextWidth = Math.max(900, Math.round(wrapper.clientWidth - 48));
      setViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
    };

    updateWidth();

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [isFullscreen]);

  const handleExternalCardClick = (equipmentId, name) => {
    setPendingRedirect({ equipmentId, name });
    setShowModal(true);
  };

  const confirmRedirect = () => {
    if (pendingRedirect && onExternalCardClick) {
      onExternalCardClick(pendingRedirect.equipmentId, pendingRedirect.name);
    }
    setShowModal(false);
    setPendingRedirect(null);
  };

  const cancelRedirect = () => {
    setShowModal(false);
    setPendingRedirect(null);
  };

  // ✅ Remove duplicates based on process_code
  const uniqueProcessSteps = useMemo(() => {
    const seen = new Set();
    return (processSteps || []).filter((step) => {
      const id = String(step.process_code);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [processSteps]);

  // ✅ Create a map of process codes to process data for quick lookup
  const processMap = useMemo(() => {
    const map = {};
    uniqueProcessSteps.forEach((step) => {
      map[String(step.process_code)] = step;
    });
    return map;
  }, [uniqueProcessSteps]);

  /**
   * ✅ MAIN FIX: Build EDGES (per dependency, NOT per node)
   * This removes the need for hardcoded hiding + fixes relation labels.
   */
  const edges = useMemo(() => {
    const list = [];

    uniqueProcessSteps.forEach((step) => {
      const to = String(step.process_code);

      (step.dependencies || []).forEach((d) => {
        const from = String(d?.depends_on?.code);
        if (!from) return;

        list.push({
          from,
          to,
          relation: d?.relation || "FS",
        });
      });
    });

    return list;
  }, [uniqueProcessSteps]);

  const selectedNodeId = useMemo(() => {
    if (!selectedProcessId) return null;
    const selectedStep = uniqueProcessSteps.find(
      (step) => String(step.process_id) === String(selectedProcessId),
    );
    return selectedStep ? String(selectedStep.process_code) : null;
  }, [uniqueProcessSteps, selectedProcessId]);

  const activeNodeId = selectedNodeId || hoveredCard || null;

const edgesFromSource = useMemo(() => {
    const map = {};
    edges.forEach((edge, idx) => {
      if (!map[edge.from]) map[edge.from] = [];
      map[edge.from].push(idx);
    });
    return map;
  }, [edges]);

  const focusedNeighbors = useMemo(() => {
    const previous = new Set();
    const next = new Set();

    if (!activeNodeId) {
      return { previous, next };
    }

    edges.forEach((edge) => {
      if (edge.to === activeNodeId) previous.add(edge.from);
      if (edge.from === activeNodeId) next.add(edge.to);
    });

    return { previous, next };
  }, [edges, activeNodeId]);

  const getNodeOpacity = (nodeId) => {
    if (!activeNodeId) return 1;
    if (nodeId === activeNodeId) return 1;
    if (focusedNeighbors.previous.has(nodeId)) return 0.92;
    if (focusedNeighbors.next.has(nodeId)) return 0.82;
    return 0.28;
  };

  const getEdgeVisual = (edge) => {
    if (!activeNodeId) {
      return { opacity: 1, strokeWidth: 2, color: CONNECTOR_COLORS.default, isActive: false };
    }

    const isDirectPrevious =
      edge.to === activeNodeId && focusedNeighbors.previous.has(edge.from);
    const isDirectNext =
      edge.from === activeNodeId && focusedNeighbors.next.has(edge.to);

    if (isDirectPrevious) {
      return { opacity: 0.95, strokeWidth: 3.5, color: CONNECTOR_COLORS.active, isActive: true };
    }

    if (isDirectNext) {
      return { opacity: 0.85, strokeWidth: 2.8, color: CONNECTOR_COLORS.active, isActive: true };
    }

    return { opacity: 0.2, strokeWidth: 2, color: CONNECTOR_COLORS.default, isActive: false };
  };


  // ✅ Identify external dependency nodes (predecessors not inside processSteps)
  const externalDependencies = useMemo(() => {
    const external = new Set();
    const validCodes = new Set(Object.keys(processMap));

    edges.forEach((e) => {
      if (!validCodes.has(String(e.from))) {
        external.add(String(e.from));
      }
    });

    return external;
  }, [edges, processMap]);

  // ✅ All nodes to show in flowchart (internal + external placeholder)
  const allNodeIds = useMemo(() => {
    return [...Object.keys(processMap), ...Array.from(externalDependencies)];
  }, [processMap, externalDependencies]);

  /**
   * ✅ Build predecessorMap ONLY for layout calculation
   * {
   *   "8036": ["8035"],
   *   "8039": ["8038","8035","8036","8037"]
   * }
   */
  const predecessorMap = useMemo(() => {
    const map = {};
    allNodeIds.forEach((id) => {
      map[String(id)] = [];
    });

    edges.forEach((e) => {
      if (!map[e.to]) map[e.to] = [];
      map[e.to].push(String(e.from));
    });

    // ✅ de-duplicate predecessor arrays
    Object.keys(map).forEach((k) => {
      map[k] = Array.from(new Set(map[k]));
    });

    return map;
  }, [edges, allNodeIds]);

  /**
   * ✅ POSITIONS FIX:
   * - Row = dependency depth
   * - Nodes in same row placed side-by-side
   */
  const positions = useMemo(() => {
    const posMap = {};
    const rowMap = {};
    const rows = {};

    const calculateRow = (nodeId, visited = new Set()) => {
      if (rowMap[nodeId] !== undefined) return rowMap[nodeId];
      if (visited.has(nodeId)) return 0;

      const preds = predecessorMap[nodeId] || [];
      if (preds.length === 0) {
        rowMap[nodeId] = 0;
        return 0;
      }

      visited.add(nodeId);

      let maxRow = 0;
      preds.forEach((p) => {
        maxRow = Math.max(maxRow, calculateRow(p, visited) + 1);
      });

      rowMap[nodeId] = maxRow;
      return maxRow;
    };

    allNodeIds.forEach((id) => calculateRow(String(id)));

    Object.entries(rowMap).forEach(([id, row]) => {
      if (!rows[row]) rows[row] = [];
      rows[row].push(id);
    });

    const cardWidth = 200;
    const verticalGap = 340;
    const minHorizontalGap = 140;
    const zoomCompensatedWidth = zoom < 1 ? viewportWidth / zoom : viewportWidth;
    const contentWidth = Math.max(1000, Math.min(2600, zoomCompensatedWidth));

    // ✅ Sort rows numerically to process in order (important for barycenter calculation)
    const sortedRowEntries = Object.entries(rows).sort((a, b) => Number(a[0]) - Number(b[0]));
    const prevRowPositions = {}; // Store X positions of nodes in previous layer

    sortedRowEntries.forEach(([rowStr, ids]) => {
      const row = Number(rowStr);
      const y = row * verticalGap + 60;

      // ✅ BARYCENTRIC SORT: Calculate average position of predecessors
      // This minimizes edge crossings by grouping connected nodes together
      const barycenters = {};
      ids.forEach((id) => {
        const preds = predecessorMap[id] || [];
        
        if (preds.length === 0) {
          // Root nodes: use numeric ID as fallback
          const num = parseInt(id);
          barycenters[id] = !isNaN(num) ? num : 0;
        } else {
          // Calculate average X position of predecessors
          let sumX = 0;
          let count = 0;
          preds.forEach((pred) => {
            if (prevRowPositions[pred] !== undefined) {
              sumX += prevRowPositions[pred];
              count++;
            }
          });
          
          if (count > 0) {
            barycenters[id] = sumX / count; // Average position
          } else {
            // Fallback if predecessors not in previous layer
            const num = parseInt(id);
            barycenters[id] = !isNaN(num) ? num : 0;
          }
        }
      });

      // ✅ Sort by barycenter to minimize edge crossings
      const sorted = [...ids].sort((a, b) => barycenters[a] - barycenters[b]);

      const rowCount = sorted.length;
      const minimumRowWidth =
        rowCount * cardWidth + Math.max(0, rowCount - 1) * minHorizontalGap;
      const rowWidth = Math.max(contentWidth, minimumRowWidth);

      sorted.forEach((id, idx) => {
        let x = 0;
        if (rowCount > 1) {
          const step = (rowWidth - cardWidth) / (rowCount - 1);
          const firstCenter = -rowWidth / 2 + cardWidth / 2;
          x = firstCenter + idx * step;
        }

        posMap[id] = {
          x: x - cardWidth / 2,
          y,
          row,
          col: idx,
        };

        // ✅ Store X position for next layer's barycenter calculation
        prevRowPositions[id] = x;
      });
    });

    return posMap;
  }, [allNodeIds, predecessorMap, viewportWidth, zoom]);

  if (!uniqueProcessSteps || uniqueProcessSteps.length === 0) {
    return (
      <div className="flowchart-no-data">
        <p>No process steps available</p>
      </div>
    );
  }

  const cardWidth = 200;
  const graphPaddingX = 80;
  const graphPaddingY = 280;
  const positionValues = Object.values(positions);
  const minNodeX = Math.min(...positionValues.map((p) => p.x));
  const maxNodeRightX = Math.max(...positionValues.map((p) => p.x + cardWidth));
  const graphWidth = Math.max(
    900,
    Math.ceil(maxNodeRightX - minNodeX + graphPaddingX * 2),
  );
  const graphMinX = minNodeX - graphPaddingX;
  const maxY = Math.max(...positionValues.map((p) => p.y)) + graphPaddingY;

 const drawConnectorPath = (fromPos, toPos, edgeIndex = 0, totalEdges = 1) => {
  const cardHeight = 200;

  const rowDiff = toPos.row - fromPos.row;
  const isLongDistance = rowDiff > 1;

  if (isLongDistance) {
  const exitX = fromPos.x + cardWidth / 2;
  const exitY = fromPos.y + cardHeight;
  const entryX = toPos.x + cardWidth / 2;
  const entryY = toPos.y;                    // ? this is correct top of card
  const bypassX = Math.max(fromPos.x, toPos.x) + cardWidth + 80;

  return `M ${exitX} ${exitY}
          L ${exitX} ${exitY + 20}
          L ${bypassX} ${exitY + 20}
          L ${bypassX} ${entryY}
          L ${entryX} ${entryY}`;  // ? removed the -20 step, go straight in
}

  // Original logic untouched below
  const spread = cardWidth * 0.6;
  const fromOffset = totalEdges === 1
    ? 0
    : (edgeIndex / (totalEdges - 1) - 0.5) * spread;

  const fromX = fromPos.x + cardWidth / 2 + fromOffset;
  const fromY = fromPos.y + cardHeight;
  const toX = toPos.x + cardWidth / 2;
  const toY = toPos.y;

  const gap = Math.max(40, toY - fromY);
  const bend = Math.min(60, gap / 2);

  return `M ${fromX} ${fromY}
          C ${fromX} ${fromY + bend},
            ${toX} ${toY - bend},
            ${toX} ${toY}`;
};
  

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const istOffset = 5.5 * 60 * 60 * 1000;
      const istDate = new Date(date.getTime() + istOffset);

      const day = String(istDate.getUTCDate()).padStart(2, "0");
      const month = istDate.toLocaleString("en-US", {
        month: "short",
        timeZone: "UTC",
      });
      const year = istDate.getUTCFullYear();

      let hours = istDate.getUTCHours();
      const minutes = String(istDate.getUTCMinutes()).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      return "N/A";
    }
  };

  // ✅ Helper function to get external dependency info
  const getExternalProcessInfo = (externalStepId) => {
    for (const step of uniqueProcessSteps) {
      if (step.dependencies && step.dependencies.length > 0) {
        for (const dep of step.dependencies) {
          if (String(dep.depends_on.code) === String(externalStepId)) {
            return {
              code: dep.depends_on.code,
              name: dep.depends_on.name,
              equipment_id: dep.depends_on.equipment_id,
              id: dep.depends_on.id,
            };
          }
        }
      }
    }
    return null;
  };

  return (
    <div
      className={`flowchart-container ${isFullscreen ? "fullscreen" : ""}`}
      style={{
        position: isFullscreen ? "fixed" : "relative",
        top: isFullscreen ? 0 : "auto",
        left: isFullscreen ? 0 : "auto",
        width: isFullscreen ? "100vw" : "auto",
        height: isFullscreen ? "100vh" : "auto",
        zIndex: isFullscreen ? 9999 : "auto",
        backgroundColor: isFullscreen ? "white" : "transparent",
      }}
    >
      {/* Legend - Hide in fullscreen */}
      {!isFullscreen && (
        <div className="flowchart-legend d-flex justify-center algin-center">
          {Object.entries(relationStyles).map(
            ([key, { arrow, label, dash }]) => (
              <div key={key} className="legend-item">
                <svg width="50" height="20" className="legend-line">
                  <line
                    x1="2"
                    y1="10"
                    x2="40"
                    y2="10"
                    stroke={arrow}
                    strokeWidth="2"
                    strokeDasharray={dash}
                  />
                  <polygon points="40,10 46,7 46,13" fill={arrow} />

                </svg>
                <span className="legend-label">{label}</span>
              </div>
            ),
          )}

          {externalDependencies.size > 0 && (
            <div className="legend-item" style={{ marginLeft: "20px" }}>
              <svg width="50" height="20">
                <rect
                  x="2"
                  y="5"
                  width="46"
                  height="10"
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeDasharray="3,3"
                  rx="2"
                />
              </svg>
              <span className="legend-label">External Process</span>
            </div>
          )}
        </div>
      )}

      {isFullscreen && (
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            zIndex: 11,
            background: "rgba(255,255,255,0.92)",
            padding: "8px 12px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            pointerEvents: "none",
          }}
        >
          <img
            src="/jecon.png"
            alt="Jecon"
            style={{ height: "32px", width: "auto", display: "block" }}
          />
        </div>
      )}

      {/* Zoom Controls */}
      <div
        style={{
          position: "absolute",
          top: isFullscreen ? "20px" : "113px",
          right: isFullscreen ? "20px" : "24px",
          display: "flex",
          gap: "8px",
          zIndex: 10,
          backgroundColor: "white",
          padding: "8px",
          borderRadius: "6px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Tooltip title="Zoom In" arrow>
          <button
            onClick={() => setZoom(Math.min(zoom + 0.1, 2))}
            style={{
              padding: "6px 10px",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              backgroundColor: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
            title="Zoom In"
          >
            <AiOutlineZoomIn className="fs-5" />
          </button>
        </Tooltip>

        <span
          style={{
            padding: "6px 8px",
            fontSize: "14px",
            fontWeight: "600",
            color: "#64748b",
          }}
        >
          {Math.round(zoom * 100)}%
        </span>

        <Tooltip title="Zoom Out" arrow>
          <button
            onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}
            style={{
              padding: "6px 10px",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              backgroundColor: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
            title="Zoom Out"
          >
            <AiOutlineZoomOut className="fs-5" />
          </button>
        </Tooltip>

        <button
          onClick={() => setZoom(1)}
          style={{
            padding: "6px 10px",
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            backgroundColor: "white",
            cursor: "pointer",
            fontSize: "14px",
          }}
          title="Reset Zoom"
        >
          Reset
        </button>

        <Tooltip
          title={isFullscreen ? "Exit Full screen view" : "Full screen view"}
          arrow
        >
          <button
            onClick={toggleFullscreen}
            style={{
              padding: "6px 10px",
              border: "1px solid #e2e8f0",
              borderRadius: "4px",
              backgroundColor: "white",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "bold",
            }}
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <RiFullscreenExitLine className="fs-5" />
            ) : (
              <RiFullscreenFill className="fs-5" />
            )}
          </button>
        </Tooltip>
      </div>

      {/* Flowchart SVG */}
      <div
        ref={flowchartWrapperRef}
        className="flowchart-svg-wrapper"
        style={{
          maxHeight: isFullscreen ? "calc(100vh - 80px)" : "600px",
          height: isFullscreen ? "calc(100vh - 80px)" : "auto",
          overflowX: "auto",
          padding: isFullscreen ? "64px 12px 12px" : "12px",
        }}
      >
        <div
          className="flowchart-svg-container"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: zoom < 1 ? "center top" : "top left",
            transition: "transform 0.2s ease",
            minWidth: `${graphWidth}px`,
            width: `${graphWidth}px`,
          }}
        >
          <svg
            width={graphWidth}
            height={maxY}
            viewBox={`${graphMinX} 0 ${graphWidth} ${maxY}`}
            preserveAspectRatio="xMinYMin meet"
            className="flowchart-svg"
          >
            {/* Arrow markers */}
            <defs>
              {[{ key: "default", color: CONNECTOR_COLORS.default }, { key: "active", color: CONNECTOR_COLORS.active }].map(({ key, color }) => (
                <marker
                  key={`marker-${key}`}
                  id={`arrowhead-${key}`}
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <polygon points="0,0 6,3 0,6" fill={color} />
                </marker>
              ))}
            </defs>

            {/* ✅ LAYER 1: Paths (use edges list) */}
<g className="paths-layer">
              {edges.map((edge, idx) => {
                const fromPos = positions[edge.from];
                const toPos = positions[edge.to];

                if (!fromPos || !toPos) return null;

                // prevent reverse arrows (optional)
                if (fromPos.row > toPos.row) return null;

                const isExternal = externalDependencies.has(edge.from);
                const relation = edge.relation || "FS";
                const style = relationStyles[relation] || relationStyles.FS;
                const sourceEdges = edgesFromSource[edge.from] || [];
                const edgeIndex = sourceEdges.indexOf(idx);
                const totalEdges = sourceEdges.length || 1;
                const path = drawConnectorPath(
                  fromPos,
                  toPos,
                  edgeIndex,
                  totalEdges,
                );

                const edgeVisual = getEdgeVisual(edge);
                return (
                  <g key={`edge-${edge.from}-${edge.to}-${idx}`}>
                    <path
                      d={path}
                      stroke={edgeVisual.color}
                      strokeWidth={edgeVisual.strokeWidth}
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeDasharray={isExternal ? "5,5" : style.dash}
                      markerEnd={`url(#arrowhead-${edgeVisual.isActive ? "active" : "default"})`}
                      opacity={isExternal ? Math.max(0.55, edgeVisual.opacity) : edgeVisual.opacity}
                      transition="opacity 0.2s ease, stroke-width 0.2s ease"
                    />
                  </g>
                );
              })}
            </g>

            {/* ✅ LAYER 2: Cards */}
            <g className="cards-layer">
              {/* External placeholder cards */}
              {Array.from(externalDependencies).map((stepId) => {
                const pos = positions[stepId];
                if (!pos) return null;

                const cardWidth = 200;
                const cardHeight = 200;

                const depInfo = getExternalProcessInfo(stepId);
                const displayName = depInfo?.name || `Process ${stepId}`;

                return (
                  <g
                    key={`external-${stepId}`}
                    onClick={() =>
                      handleExternalCardClick(
                        depInfo?.equipment_id,
                        depInfo?.name,
                      )
                    }
                    onMouseEnter={() => setHoveredCard(stepId)}
                    onMouseLeave={() => setHoveredCard(null)}
                    style={{
                      cursor: onExternalCardClick ? "pointer" : "default",
                    }}
                    opacity={getNodeOpacity(stepId)}
                  >
                    <rect
                      x={pos.x}
                      y={pos.y}
                      width={cardWidth}
                      height={cardHeight}
                      fill="#f8fafc"
                      stroke="#94a3b8"
                      strokeWidth="2"
                      strokeDasharray="5,5"
                      rx="6"
                      filter="drop-shadow(0 1px 2px rgba(0,0,0,0.05))"
                    />

                    <foreignObject
                      x={pos.x + 10}
                      y={pos.y + 8}
                      width={cardWidth - 20}
                      height="20"
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "9px",
                          fontWeight: "600",
                          color: "#94a3b8",
                        }}
                      >
                        <span>Step {stepId}</span>
                      </div>
                    </foreignObject>

                    <foreignObject
                      x={pos.x + 10}
                      y={pos.y + 35}
                      width={cardWidth - 20}
                      height="70"
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          fontWeight: "500",
                          color: "#64748b",
                          lineHeight: "1.4",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {displayName}
                      </div>
                    </foreignObject>

                    <foreignObject
                      x={pos.x + 10}
                      y={pos.y + 95}
                      width={cardWidth - 20}
                      height="20"
                    >
                      <div
                        style={{
                          fontSize: "9px",
                          color: "#94a3b8",
                          fontStyle: "italic",
                        }}
                      >
                        Different Equipment
                      </div>
                    </foreignObject>
                  </g>


                );
              })}

              {/* Main cards */}
              {uniqueProcessSteps.map((step) => {
  const stepId = String(step.process_code);
  const pos = positions[stepId];
  if (!pos) return null;

  const CARD_W = 200;
  const CARD_H = 200;

  const sc = getStatusInfo(step);
  const delay = getDurationAnalysis(step);
  const isSelected = String(step.process_id) === String(selectedProcessId);
  const nodeOpacity = getNodeOpacity(stepId);

  // Step label e.g. S-01
  const stepLabel = `S-${String(step.process_code).slice(-2).padStart(2, "0")}`;

  return (
    <g
      key={`step-${step.process_code}`}
      onClick={() => onCardClick && onCardClick(step.process_id)}
      onMouseEnter={() => setHoveredCard(stepId)}
      onMouseLeave={() => setHoveredCard(null)}
      style={{ cursor: onCardClick ? "pointer" : "default" }}
      opacity={nodeOpacity}
    >
      {/* -- Card shadow (fake, rendered as offset rect) -- */}
      <rect
        x={pos.x + 2}
        y={pos.y + 3}
        width={CARD_W}
        height={CARD_H}
        rx="10"
        fill="rgba(0,0,0,0.06)"
      />

      {/* -- Card body -- */}
      <rect
        x={pos.x}
        y={pos.y}
        width={CARD_W}
        height={CARD_H}
        rx="10"
        fill="#ffffff"
        stroke={isSelected ? "#b71c1c" : sc.cardBorder}
        strokeWidth={isSelected ? 2 : 1}
      />

      {/* -- Status badge + Step code row -- */}
      <foreignObject
        x={pos.x + 8}
        y={pos.y + 8}
        width={CARD_W - 16}
        height={30}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            overflow: "visible",
          }}
        >
          {/* Status pill */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              borderRadius: 999,
              padding: "0 10px",
              minHeight: 20,
              fontSize: 10,
              fontWeight: 600,
              color: sc.textColor,
              whiteSpace: "nowrap",
              lineHeight: 1,
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            <span style={{ fontSize: 9, lineHeight: 1 }}>{sc.icon}</span>
            <span>{sc.label}</span>
          </div>

          {/* Step code */}
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#94a3b8",
              letterSpacing: "0.4px",
            }}
          >
            {stepLabel}
          </span>
        </div>
      </foreignObject>

      {/* -- Divider -- */}
      <line
        x1={pos.x + 10}
        y1={pos.y + 38}
        x2={pos.x + CARD_W - 10}
        y2={pos.y + 38}
        stroke="#f1f5f9"
        strokeWidth={1}
      />

      {/* -- Process name -- */}
      <foreignObject
        x={pos.x + 10}
        y={pos.y + 42}
        width={CARD_W - 20}
        height={44}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.35,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}
        >
          {step.process_name}
        </div>
      </foreignObject>

      {/* -- Duration row -- */}
      <foreignObject
        x={pos.x + 10}
        y={pos.y + 90}
        width={CARD_W - 20}
        height={36}
      >
        <div>
          <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500 }}>
            Duration
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 6,
              marginTop: 2,
            }}
          >
            <span style={{ fontSize: 16, fontWeight: 700, color: "#1e293b" }}>
              {step.durations_eta} hrs
            </span>
            {delay && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: delay.color,
                }}
              >
                {delay.label}
              </span>
            )}
          </div>
        </div>
      </foreignObject>

      {/* -- Divider -- */}
      <line
        x1={pos.x + 10}
        y1={pos.y + 130}
        x2={pos.x + CARD_W - 10}
        y2={pos.y + 130}
        stroke="#f1f5f9"
        strokeWidth={1}
      />

      {/* -- Start date -- */}
      <foreignObject
        x={pos.x + 10}
        y={pos.y + 134}
        width={CARD_W - 20}
        height={30}
      >
        <div>
          <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 500 }}>
            Start
          </div>
          <div
            style={{ fontSize: 10, color: "#334155", fontWeight: 500, marginTop: 1 }}
          >
            {formatDate(step.actual_start || step.start_eta)}
          </div>
        </div>
      </foreignObject>

      {/* -- Footer: Started By / Ended By -- */}
      <line
        x1={pos.x + 10}
        y1={pos.y + CARD_H - 30}
        x2={pos.x + CARD_W - 10}
        y2={pos.y + CARD_H - 30}
        stroke="#f1f5f9"
        strokeWidth={1}
      />
      <foreignObject
        x={pos.x + 10}
        y={pos.y + CARD_H - 28}
        width={CARD_W - 20}
        height={24}
      >
        <div
          style={{
            fontSize: 9,
            color: "#64748b",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <div>
            <strong style={{ color: "#475569" }}>
              {step.ended_by ? "Ended By" : "Started By"}
            </strong>{" "}
            {step.ended_by || step.started_by || "Pending"}
          </div>
        </div>
      </foreignObject>
    </g>
  );
})}
            </g>

            {/* ? LAYER 1.5: Relation Labels */}
           <g className="labels-layer">
              {edges.map((edge, idx) => {
                const fromPos = positions[edge.from];
                const toPos = positions[edge.to];
                if (!fromPos || !toPos) return null;

                if (fromPos.row > toPos.row) return null;

                const relation = edge.relation || "FS";
                const style = relationStyles[relation] || relationStyles.FS;
                const relationTooltip =
                  relationTooltips[relation] || relationTooltips.FS;

                const cardWidth = 200;
                const cardHeight = 200;

                const sourceEdges = edgesFromSource[edge.from] || [];
                const edgeIndex = sourceEdges.indexOf(idx);
                const totalEdges = sourceEdges.length || 1;

                const connectorPath = drawConnectorPath(
                  fromPos,
                  toPos,
                  edgeIndex,
                  totalEdges,
                );
                if (!connectorPath) return null;

        const spread = cardWidth * 0.6;
                const fromOffset =
                  totalEdges === 1 ? 0 : (edgeIndex / (totalEdges - 1) - 0.5) * spread;
                const fromX = fromPos.x + cardWidth / 2 + fromOffset;
                const fromY = fromPos.y + cardHeight;
                const toX = toPos.x + cardWidth / 2;
                const toY = toPos.y;

                const verticalDistance = toY - fromY;
                const gap = Math.max(40, verticalDistance);
                const bend = Math.min(60, gap / 2);

                const t = 0.5;
                const t2 = t * t;
                const t3 = t2 * t;
                const mt = 1 - t;
                const mt2 = mt * mt;
                const mt3 = mt2 * mt;

                const p0x = fromX;
                const p0y = fromY;
                const p1x = fromX;
                const p1y = fromY + bend;
                const p2x = toX;
                const p2y = toY - bend;
                const p3x = toX;
                const p3y = toY;
                const rowDiff = toPos.row - fromPos.row;
                const isLongDistance = rowDiff > 1;

                let labelX, labelY;

                if (isLongDistance) {
                  const exitY = fromPos.y + cardHeight;
                  const entryY = toPos.y;
                  const bypassX = Math.max(fromPos.x, toPos.x) + cardWidth + 80;

                  labelX = bypassX;                        // on the vertical bypass line
                  labelY = (exitY + entryY) / 2;          // middle of the vertical segment
                } else {
                  // your existing bezier midpoint calculation stays here unchanged
                  labelX = mt3 * p0x + 3 * mt2 * t * p1x + 3 * mt * t2 * p2x + t3 * p3x;
                  labelY = mt3 * p0y + 3 * mt2 * t * p1y + 3 * mt * t2 * p2y + t3 * p3y;
                }

                const labelVisual = getEdgeVisual(edge);

                return (
                  <g
                    key={`label-${edge.from}-${edge.to}-${idx}`}
                    opacity={labelVisual.opacity}
                    transition="opacity 0.2s ease"
                  >
                    <title>{relationTooltip}</title>
                    <rect
                      x={labelX - 18}
                      y={labelY - 10}
                      width="36"
                      height="20"
                      fill="white"
                      rx="3"
                      stroke={labelVisual.color}
                      strokeWidth="1"
                    />
                    <text
                      x={labelX}
                      y={labelY + 4}
                      fontSize="14"
                      fontWeight="900"
                      fill={labelVisual.color}
                      textAnchor="middle"
                    >
                      {style.label}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* External Dependencies Info Panel */}
      {/* {externalDependencies.size > 0 && (
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "#f8fafc",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: "#475569",
              marginBottom: "10px",
            }}
          >
            External Dependencies ({externalDependencies.size})
          </h4>
          <div style={{ fontSize: "12px", color: "#64748b" }}>
            {Array.from(externalDependencies).map((stepId) => {
              const info = getExternalProcessInfo(stepId);
              return (
                <div
                  key={stepId}
                  style={{
                    marginBottom: "8px",
                    paddingBottom: "8px",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <strong>Step {stepId}:</strong>{" "}
                  {info?.name || "External Process"}
                </div>
              );
            })}
          </div>
        </div>
      )} */}

      {/* Confirmation Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              maxWidth: "400px",
              width: "90%",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Confirm Redirect
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: "14px",
                color: "#6b7280",
                lineHeight: "1.5",
              }}
            >
              Are you sure you want to redirect to the respective equipment
              number?
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={cancelRedirect}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  color: "#6b7280",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#f9fafb";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "white";
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRedirect}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "6px",
                  backgroundColor: "#3b82f6",
                  color: "white",
                  fontSize: "14px",
                  cursor: "pointer",
                  transition: "background-color 0.2s",
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#2563eb";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#3b82f6";
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessFlowchartVisualization;

